import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api, apiBlob, mensagemErro } from '../api';
import { Carregando, Erro, Icon, Modal, Progresso, TagChip, Toast, useToast } from '../components/ui';
import { dataHora, diasDesde, formatarWhatsapp, moeda, relativo } from '../format';
import { copiarImagem, paraPng, podeCopiarImagem } from '../imagem';
import { abrirWhatsapp } from '../whatsapp';
import type { Tag } from './Clientes';

interface Proximo {
  campanha: { id: string; nome: string; status: string; temImagem: boolean };
  progresso: { total: number; enviados: number; pendentes: number };
  item: null | {
    id: string;
    status: 'PENDENTE' | 'ABERTO';
    pendenteConfirmacao: boolean;
    abertoEm: string | null;
    mensagem: string;
    faltando: string[];
    cliente: {
      id: string; nome: string; empresa: string | null; cidade: string | null; whatsapp: string;
      ultimaCompra: string | null; valorUltimaCompra: string | null; ultimoContato: string | null; tags: Tag[];
      ultimaAtividade: null | { descricao: string; tipo: string; usuario: string | null; createdAt: string };
    };
  };
}

const RESULTADOS = [
  { id: 'INTERESSADO', label: 'Interessado' },
  { id: 'NAO_INTERESSADO', label: 'Sem interesse' },
  { id: 'RETORNAR', label: 'Retornar' },
] as const;

export function FilaPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['fila', id], queryFn: () => api<Proximo>(`/campanhas/${id}/fila/proximo`), staleTime: 0 });
  const item = q.data?.item ?? null;
  // PNG pronto antes do clique: a cópia precisa acontecer enquanto a aba ainda está em foco
  const imagem = useQuery({
    queryKey: ['campanha-imagem', id],
    queryFn: async () => paraPng(await apiBlob(`/campanhas/${id}/imagem`)),
    enabled: !!q.data?.campanha.temImagem,
    staleTime: Infinity,
  });
  const [imagemUrl, setImagemUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!imagem.data) return;
    const u = URL.createObjectURL(imagem.data);
    setImagemUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [imagem.data]);

  const [msg, setMsg] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [aguardando, setAguardando] = useState(false);
  const [pulando, setPulando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // Novo item: reseta o estado da tela
  useEffect(() => {
    setMsg(item?.mensagem ?? '');
    setResultado(null);
    setErro(null);
    setAguardando(false);
    setConfirmando(!!item?.pendenteConfirmacao); // RN-CAM-02: item ABERTO volta pedindo confirmação
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // RN-CAM-03: ao voltar do WhatsApp, pergunta se enviou
  useEffect(() => {
    const vis = () => {
      if (document.visibilityState === 'visible' && aguardando) setConfirmando(true);
    };
    document.addEventListener('visibilitychange', vis);
    return () => document.removeEventListener('visibilitychange', vis);
  }, [aguardando]);

  const proximo = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['fila', id] });
    void qc.invalidateQueries({ queryKey: ['campanha', id] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  }, [qc, id]);

  async function abrir() {
    if (!item) return;
    setErro(null);
    if (imagem.data && !(await copiarImagem(imagem.data))) {
      toast.show('Não consegui copiar a imagem. Use "Copiar imagem" e cole no WhatsApp.');
    }
    try {
      await abrirWhatsapp(async () => (await api<{ url: string }>(`/campanha-itens/${item.id}/abrir`, { body: { mensagem: msg } })).url);
      setAguardando(true);
      // Em desktop o foco pode não sair da aba: mostra a confirmação após um instante
      setTimeout(() => setConfirmando(true), 1500);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CLIENTE_NAO_CONTACTAR') {
        toast.show('Cliente pediu para não ser contatado. Ele saiu da fila.');
        await proximo();
      } else setErro(mensagemErro(e));
    }
  }

  const enviei = useCallback(async () => {
    if (!item || ocupado) return;
    setOcupado(true);
    try {
      const r = await api<{ oportunidadeId: string | null; tarefaId: string | null }>(`/campanha-itens/${item.id}/enviado`, {
        body: { mensagemFinal: msg, resultado: resultado ?? 'ENVIADO', followUpDias: followUp ? 2 : 0 },
      });
      setConfirmando(false);
      toast.show(r.oportunidadeId ? 'Enviado · oportunidade criada.' : r.tarefaId ? 'Enviado · retorno agendado.' : 'Enviado.');
      await proximo();
    } catch (e) {
      setErro(mensagemErro(e));
      setConfirmando(false);
    } finally {
      setOcupado(false);
    }
  }, [item, ocupado, msg, resultado, followUp, toast, proximo]);

  async function pular(motivo: string) {
    if (!item) return;
    try {
      await api(`/campanha-itens/${item.id}/pular`, { body: { motivo } });
      setPulando(false);
      setConfirmando(false);
      await proximo();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  // Atalho: Enter confirma o envio enquanto o painel de confirmação está aberto
  useEffect(() => {
    if (!confirmando) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        void enviei();
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [confirmando, enviei]);

  if (q.isLoading) return <Carregando />;
  if (q.error) return <div style={{ padding: 16 }}><Erro erro={mensagemErro(q.error)} /><Link to="/campanhas">Voltar</Link></div>;
  const d = q.data!;
  const pg = d.progresso;
  const c = item?.cliente;
  const dias = diasDesde(c?.ultimaCompra);

  return (
    <div className="fila">
      <header>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Link to={`/campanhas/${d.campanha.id}`} className="btn icon-btn" aria-label="Voltar para a campanha" style={{ border: 0, background: 'transparent', width: 44, height: 44 }}><Icon name="voltar" size={20} /></Link>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{d.campanha.nome}</span>
            <span className="num small muted">{pg.enviados} de {pg.total} enviados · faltam {pg.pendentes}</span>
          </div>
          <Link to={`/campanhas/${d.campanha.id}`} className="btn icon-btn" aria-label="Ver lista da campanha" style={{ marginLeft: 'auto', border: 0, background: 'transparent', width: 44, height: 44 }}><Icon name="lista" size={20} /></Link>
        </div>
        <Progresso valor={pg.total - pg.pendentes} total={pg.total} />
      </header>

      {!item || !c ? (
        <div className="corpo">
          <div className="empty" style={{ marginTop: 40 }}>
            <Icon name="check" size={32} color="var(--green-text)" />
            <h2 style={{ fontSize: 20, color: 'var(--text)' }}>
              {d.campanha.status === 'PAUSADA' ? 'Campanha pausada' : 'Fila concluída'}
            </h2>
            <span>{d.campanha.status === 'PAUSADA' ? 'O administrador pausou esta campanha.' : 'Você zerou sua fila nesta campanha.'}</span>
            <Link className="btn primary lg" to="/">Voltar ao início</Link>
          </div>
        </div>
      ) : (
        <>
          <div className="corpo">
            <Erro erro={erro} />
            {d.campanha.status === 'PAUSADA' && <div className="alert warn">Campanha pausada: você só pode confirmar envios já abertos.</div>}
            <section className="card" style={{ padding: 16, borderRadius: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="logo" style={{ width: 44, height: 44, borderRadius: 22, background: '#1E293B', color: 'var(--blue-text)', fontSize: 14 }}>
                  {c.nome.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Link to={`/clientes/${c.id}`} style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600 }}>{c.nome}</Link>
                  <span className="small muted">{[c.empresa, c.cidade].filter(Boolean).join(' · ') || formatarWhatsapp(c.whatsapp)}</span>
                </div>
              </div>
              {c.tags.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{c.tags.map((t) => <TagChip key={t.id} {...t} />)}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                <div className="mini"><span className="l">Última compra</span><span className="num">{dias === null ? 'sem registro' : dias === 0 ? 'hoje' : `há ${dias} dias`}</span><span className="num small muted">{moeda(c.valorUltimaCompra)}</span></div>
                <div className="mini"><span className="l">Último contato</span><span className="num">{relativo(c.ultimoContato)}</span><span className="num small muted">{c.ultimoContato ? dataHora(c.ultimoContato) : '—'}</span></div>
              </div>
              {c.ultimaAtividade && (
                <div className="small muted" style={{ padding: 10, borderLeft: '2px solid #334155', background: '#101215' }}>
                  “{c.ultimaAtividade.descricao}” — {c.ultimaAtividade.usuario ?? 'Sistema'}, {dataHora(c.ultimaAtividade.createdAt)}
                </div>
              )}
            </section>

            <label className="field">
              <span style={{ display: 'flex' }}>Mensagem<span className="faint" style={{ marginLeft: 'auto' }}>você pode editar</span></span>
              <textarea className="textarea" rows={6} value={msg} onChange={(e) => setMsg(e.target.value)} style={{ fontSize: 15, borderRadius: 12 }} />
              {item.faltando.length > 0 && <span className="hint warn">Sem dado para: {item.faltando.join(', ')}. Revise o texto antes de enviar.</span>}
            </label>

            {d.campanha.temImagem && (
              <div className="field">
                <span>Imagem</span>
                {imagemUrl
                  ? <img src={imagemUrl} alt="Imagem da campanha" style={{ maxHeight: 160, maxWidth: '100%', objectFit: 'contain', alignSelf: 'flex-start', borderRadius: 10 }} />
                  : <span className="small muted">{imagem.error ? 'Não foi possível carregar a imagem.' : 'Carregando imagem…'}</span>}
                {podeCopiarImagem()
                  ? <span className="hint">Ao abrir o WhatsApp a imagem já vai copiada: na conversa, aperte <b>Ctrl+V</b> (ou segure e “Colar” no celular).</span>
                  : <span className="hint warn">Este navegador não copia imagens. Anexe pelo clipe 📎 no WhatsApp.</span>}
                {imagem.data && podeCopiarImagem() && (
                  <button type="button" className="btn" style={{ alignSelf: 'flex-start' }}
                    onClick={async () => toast.show((await copiarImagem(imagem.data!)) ? 'Imagem copiada. Cole no WhatsApp.' : 'Não foi possível copiar a imagem.')}>
                    Copiar imagem
                  </button>
                )}
              </div>
            )}
          </div>

          <footer>
            {item.status === 'ABERTO' && !confirmando
              ? <button className="btn primary xl" onClick={() => setConfirmando(true)}>Confirmar envio</button>
              : <button className="btn primary xl" onClick={abrir} disabled={d.campanha.status !== 'EM_EXECUCAO' || !msg.trim()}><Icon name="whatsapp" size={20} />Abrir WhatsApp</button>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              <button className="btn lg" onClick={() => setPulando(true)}>Pular</button>
              {item.status === 'ABERTO'
                ? <button className="btn lg" onClick={abrir} disabled={d.campanha.status !== 'EM_EXECUCAO'}>Abrir de novo</button>
                : <Link className="btn lg" to={`/clientes/${c.id}`}>Ver ficha</Link>}
            </div>
          </footer>
        </>
      )}

      {confirmando && item && c && (
        <Modal titulo="Confirmar envio" sheet onClose={() => setConfirmando(false)}>
          <div>
            <h2 style={{ fontSize: 20 }}>Enviou para {c.nome.split(' ')[0]}?</h2>
            <span className="small muted">{c.empresa ?? formatarWhatsapp(c.whatsapp)}</span>
          </div>
          <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <legend className="small muted" style={{ marginBottom: 8, padding: 0 }}>Como foi? (opcional)</legend>
            <div className="opts">
              {RESULTADOS.map((r) => (
                <button key={r.id} className={`opt${resultado === r.id ? ' on' : ''}`} aria-pressed={resultado === r.id}
                  onClick={() => setResultado(resultado === r.id ? null : r.id)}>{r.label}</button>
              ))}
            </div>
            {resultado === 'INTERESSADO' && <span className="small" style={{ color: 'var(--blue-text)' }}>Cria uma oportunidade ligada a esta campanha.</span>}
          </fieldset>
          <label className="check" style={{ padding: '12px 14px', background: 'var(--surface-2)', border: '1px solid #23272f', borderRadius: 10, justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', flexDirection: 'column' }}><span>Agendar retorno</span><span className="small muted">daqui a 2 dias, às 09:00</span></span>
            <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} style={{ width: 22, height: 22 }} />
          </label>
          <button className="btn primary xl" onClick={enviei} disabled={ocupado}>{ocupado ? 'Registrando…' : 'Sim, enviei · próximo'}</button>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            <button className="btn lg" onClick={() => { setConfirmando(false); setPulando(true); }}>Não, pular</button>
            <button className="btn lg" onClick={() => { setConfirmando(false); setAguardando(false); }}>Ainda não</button>
          </div>
        </Modal>
      )}

      {pulando && (
        <Modal titulo="Pular cliente" sheet onClose={() => setPulando(false)}>
          <h2 style={{ fontSize: 18 }}>Por que pular?</h2>
          <button className="btn lg" onClick={() => pular('NUMERO_ERRADO')}>Número errado / sem WhatsApp</button>
          <button className="btn lg" onClick={() => pular('JA_FALEI_HOJE')}>Já falei com ele hoje</button>
          <button className="btn lg" onClick={() => pular('OUTRO')}>Outro motivo</button>
        </Modal>
      )}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </div>
  );
}
